import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Plus, Power, RotateCcw, Upload } from 'lucide-react';
import { DataTable, type Column } from '../components/data-table';
import { FormFooter, Grid, PageHeader, Row, Section, Tabs, Toggle } from '../components/form';
import { Alert, Badge, Button, Input, Select } from '../components/ui';
import { api } from '../lib/api';

interface BackupFile {
  name: string;
  sizeBytes: number;
  createdAt: string;
  comment: string;
}

const megabytes = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(1)} MB`;

/**
 * Maintenance: backups and upgrades.
 *
 * The two operations an administrator reaches for on their worst day, which is why both say what
 * they will cost before they do it rather than afterwards.
 */
export function Maintenance() {
  const [tab, setTab] = useState('backup');

  return (
    <div className="space-y-5">
      <PageHeader
        title="Maintenance"
        description="Back up and restore this PBX, and keep it up to date."
      />

      <Tabs
        tabs={[
          { id: 'backup', label: 'Backup and Restore' },
          { id: 'reboot', label: 'Reboot' },
          { id: 'upgrade', label: 'Upgrade' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'backup' ? <BackupList /> : tab === 'reboot' ? <RebootPanel /> : <UpgradePanel />}
    </div>
  );
}

function BackupList() {
  const queryClient = useQueryClient();
  const upload = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const { data: backups = [], isLoading } = useQuery({
    queryKey: ['backups'],
    queryFn: () => api.get<BackupFile[]>('/api/v1/backups'),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['backups'] });

  const create = useMutation({
    mutationFn: () => api.post<{ name: string }>('/api/v1/backups', { label: 'manual' }),
    onSuccess: (result) => {
      setNote(`Backup created: ${result.name}`);
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: async (rows: readonly BackupFile[]) => {
      for (const row of rows) await api.del(`/api/v1/backups/${encodeURIComponent(row.name)}`);
    },
    onSuccess: invalidate,
  });

  /**
   * Restore, with the cost stated before the confirmation rather than after it.
   *
   * Two things are irreversible here and both are named: everything created since the backup is
   * discarded, and every call in progress ends when the API restarts. An administrator who is
   * told this and proceeds has made a decision; one who finds out afterwards has had an accident.
   */
  const restore = useMutation({
    mutationFn: async (name: string) => {
      setBusy(name);
      return api.post<{ rollback: string | null }>(
        `/api/v1/backups/${encodeURIComponent(name)}/restore`,
        { confirm: 'restore' },
      );
    },
    onSuccess: (result) => {
      setBusy(null);
      setNote(
        `Restored. The API is restarting${result.rollback ? `; the previous database was saved as ${result.rollback}` : ''}.`,
      );
      // The server is on its way down, so a refetch now would fail. The list reloads when the
      // browser reconnects, which is also the signal that the restart finished.
      setTimeout(invalidate, 8000);
    },
    onError: () => setBusy(null),
  });

  const columns: ReadonlyArray<Column<BackupFile>> = [
    {
      key: 'name',
      header: 'File name',
      sort: (a, b) => a.name.localeCompare(b.name),
      render: (b) => <span className="font-mono text-xs">{b.name}</span>,
    },
    {
      key: 'createdAt',
      header: 'Backup time',
      sort: (a, b) => a.createdAt.localeCompare(b.createdAt),
      render: (b) => new Date(b.createdAt).toLocaleString(),
    },
    {
      key: 'size',
      header: 'Size',
      width: '7rem',
      sort: (a, b) => a.sizeBytes - b.sizeBytes,
      render: (b) => <span className="tabular-nums">{megabytes(b.sizeBytes)}</span>,
    },
    {
      key: 'comment',
      header: 'Comments',
      render: (b) => <span className="text-muted-foreground">{b.comment}</span>,
    },
    {
      key: 'actions',
      header: '',
      width: '11rem',
      render: (b) => (
        <span className="flex gap-1">
          <a
            href={`/api/v1/backups/${encodeURIComponent(b.name)}/download`}
            className="inline-flex h-8 items-center rounded-md px-2 text-sm hover:bg-accent"
            title="Download"
          >
            <Download className="size-4" />
          </a>
          <Button
            variant="ghost"
            className="h-8 px-2"
            disabled={busy !== null}
            title="Restore this backup"
            onClick={() => {
              if (
                confirm(
                  `Restore ${b.name}?\n\n` +
                    'Everything created since this backup is discarded, and every call in progress ends when the API restarts.\n\n' +
                    'The current database is saved first, so this can be undone.',
                )
              ) {
                restore.mutate(b.name);
              }
            }}
          >
            <RotateCcw className="size-4" />
          </Button>
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {note && <Alert>{note}</Alert>}
      {create.error && <Alert>{(create.error as Error).message}</Alert>}
      {restore.error && <Alert>{(restore.error as Error).message}</Alert>}
      {busy && <Alert>Restoring {busy}. The API will restart when it finishes.</Alert>}

      <input
        ref={upload}
        type="file"
        accept=".dump"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (!file) return;
          const form = new FormData();
          form.append('file', file);
          try {
            const response = await fetch('/api/v1/backups/upload', {
              method: 'POST',
              body: form,
              credentials: 'same-origin',
              headers: { 'x-csrf-token': document.cookie.match(/csrf=([^;]+)/)?.[1] ?? '' },
            });
            const body = (await response.json()) as { error?: string };
            setNote(
              response.ok ? 'Backup uploaded and verified.' : (body.error ?? 'Upload failed'),
            );
            invalidate();
          } finally {
            e.target.value = '';
          }
        }}
      />

      <DataTable
        rows={backups}
        columns={columns}
        rowKey={(b) => b.name}
        search={(b) => `${b.name} ${b.comment}`}
        searchPlaceholder="Search backups"
        toolbar={
          <span className="flex gap-2">
            <Button onClick={() => create.mutate()} disabled={create.isPending}>
              <Plus className="mr-1.5 size-4" />
              {create.isPending ? 'Backing up…' : 'Back up now'}
            </Button>
            <Button variant="secondary" onClick={() => upload.current?.click()}>
              <Upload className="mr-1.5 size-4" />
              Upload
            </Button>
          </span>
        }
        onDelete={(rows) => remove.mutate(rows)}
        deleteLabel={(rows) =>
          rows.length === 1
            ? `Delete ${rows[0]!.name}? It cannot be recovered.`
            : `Delete ${rows.length} backups? They cannot be recovered.`
        }
        empty={
          isLoading ? null : (
            <div className="py-10 text-center">
              <p className="font-medium">No backups yet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                One is taken automatically every night. Use Back up now before making a big change.
              </p>
            </div>
          )
        }
      />

      <Section title="What a backup contains" description="And, as importantly, what it does not.">
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Everything in the database</strong> — extensions,
            trunks, routes, call flows, call history, users and settings.
          </li>
          <li>
            <strong className="text-foreground">Not the audio.</strong> Recordings, voicemail and
            uploaded prompts are plain files under the data directory, and they are far too large to
            belong in a database dump. Back those up with the rest of your filesystem, or run{' '}
            <code>klinkpbx backup</code> on the host, which archives both.
          </li>
          <li>
            A dump is taken automatically each night and the most recent few are kept. Use{' '}
            <strong className="text-foreground">Back up now</strong> before a change you might want
            to undo.
          </li>
        </ul>
      </Section>
    </div>
  );
}

interface SystemInfo {
  version: string;
  asterisk: string | null;
}

interface Release {
  version: string;
  channel: 'stable' | 'beta';
  releasedAt: string;
  notesUrl?: string;
  notes?: string;
  minUpgradeFrom?: string;
}

interface UpgradeJob {
  stage: 'idle' | 'requested' | 'running' | 'succeeded' | 'failed';
  version: string | null;
  message: string | null;
}

interface UpdateInfo {
  currentVersion: string;
  channel: 'stable' | 'beta';
  lastCheckedAt: string | null;
  lastError: string | null;
  available: Release | null;
  newer: Release[];
  configured: boolean;
  job: UpgradeJob;
  settings: { policy: 'never' | 'notify' | 'auto'; channel: 'stable' | 'beta'; checkAt: string };
}

/**
 * Upgrades.
 *
 * The button here does not do the upgrade. It records that one was asked for, and a small agent
 * outside the container — the only component on the box with the Docker socket — collects the
 * request and runs it. That split is the whole design: the API is the most exposed process here,
 * and a socket in it would turn any flaw into root on the host, for every customer at once.
 *
 * What can be asked for is a version, never an image. The digests come from a manifest whose
 * Ed25519 signature was verified server-side, so an admin session cannot be talked into running an
 * arbitrary container.
 */
function UpgradePanel() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<UpdateInfo['settings'] | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const { data: info } = useQuery({
    queryKey: ['system-updates'],
    queryFn: () => api.get<UpdateInfo>('/api/v1/system/updates'),
    // Only while an upgrade is moving. The API is replaced part-way through, so the poll is also
    // how the page notices it has come back.
    refetchInterval: (query) => {
      const stage = (query.state.data as UpdateInfo | undefined)?.job.stage;
      return stage === 'requested' || stage === 'running' ? 5000 : false;
    },
    retry: true,
  });

  const settings = form ?? info?.settings ?? null;

  const check = useMutation({
    mutationFn: () => api.post<UpdateInfo>('/api/v1/system/updates/check', {}),
    onSuccess: (result) => {
      setNote(
        result.available ? `${result.available.version} is available.` : 'This PBX is up to date.',
      );
      void queryClient.invalidateQueries({ queryKey: ['system-updates'] });
    },
  });

  const install = useMutation({
    mutationFn: (version: string) =>
      api.post('/api/v1/system/updates/install', { version, confirm: 'upgrade' }),
    onSuccess: () => {
      setNote('Upgrade requested. The host agent will pick it up within about 30 seconds.');
      void queryClient.invalidateQueries({ queryKey: ['system-updates'] });
    },
  });

  const save = useMutation({
    mutationFn: () => api.put('/api/v1/system/updates/settings', settings),
    onSuccess: () => {
      setForm(null);
      setNote('Update policy saved.');
      void queryClient.invalidateQueries({ queryKey: ['system-updates'] });
    },
  });

  const dismiss = useMutation({
    mutationFn: () => api.post('/api/v1/system/updates/acknowledge', {}),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['system-updates'] }),
  });

  const job = info?.job;
  const busy = job?.stage === 'requested' || job?.stage === 'running';

  return (
    <div className="space-y-5">
      {note && <Alert>{note}</Alert>}
      {check.error && <Alert>{(check.error as Error).message}</Alert>}
      {install.error && <Alert>{(install.error as Error).message}</Alert>}

      {/*
        Signature failures are shown as prominently as an available update, and worded as a
        refusal rather than an error. A PBX that cannot verify the channel installs nothing, and
        somebody needs to know that is a deliberate stop rather than a network blip.
      */}
      {info?.lastError && (
        <Alert>
          <strong>Could not read the update channel.</strong> {info.lastError}
        </Alert>
      )}

      {job && job.stage !== 'idle' && (
        <Section title="Upgrade in progress">
          <div className="flex flex-wrap items-center gap-3">
            <Badge
              tone={
                job.stage === 'failed'
                  ? 'destructive'
                  : job.stage === 'succeeded'
                    ? 'success'
                    : 'warning'
              }
            >
              {job.stage}
            </Badge>
            <span className="text-sm">
              {job.version ? `${job.version}: ` : ''}
              {job.message ?? '—'}
            </span>
            {!busy && (
              <Button
                variant="secondary"
                className="h-8 px-2 text-sm"
                onClick={() => dismiss.mutate()}
              >
                Dismiss
              </Button>
            )}
          </div>
        </Section>
      )}

      <Section title="This PBX">
        <div className="grid gap-4 sm:grid-cols-2">
          <Row label="klinkpbx version">
            <div className="flex h-9 items-center gap-2">
              <Badge>{info?.currentVersion ?? 'unknown'}</Badge>
              {info?.available && <Badge tone="warning">{info.available.version} available</Badge>}
            </div>
          </Row>
          <Row label="Last checked">
            <div className="flex h-9 items-center text-sm text-muted-foreground">
              {info?.lastCheckedAt ? new Date(info.lastCheckedAt).toLocaleString() : 'never'}
            </div>
          </Row>
        </div>

        <div className="mt-2 flex items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => check.mutate()}
            disabled={check.isPending || !info?.configured}
          >
            <RotateCcw className={`mr-1.5 size-4 ${check.isPending ? 'animate-spin' : ''}`} />
            Check for updates
          </Button>
          {!info?.configured && (
            <span className="text-sm text-muted-foreground">
              No update channel is configured on this deployment.
            </span>
          )}
        </div>
      </Section>

      {info && info.newer.length > 0 && (
        <Section title="Available versions" description="Newest first.">
          <div className="divide-y divide-border rounded-[--radius] border border-border">
            {info.newer.map((release) => {
              // Listed but not installable: the release declares a floor this PBX has not reached.
              // Saying so is more useful than hiding it, because the admin can see the path.
              const blocked =
                release.version !== info.available?.version && info.available !== null;
              const gated =
                release.minUpgradeFrom !== undefined && release.version !== info.available?.version;

              return (
                <div key={release.version} className="flex flex-wrap items-center gap-3 p-4">
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 font-medium">
                      {release.version}
                      <Badge tone={release.channel === 'beta' ? 'warning' : 'muted'}>
                        {release.channel === 'beta' ? 'Beta' : 'GA'}
                      </Badge>
                    </p>
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      Released {new Date(release.releasedAt).toLocaleDateString()}
                      {gated ? ` — needs ${release.minUpgradeFrom} or newer first` : ''}
                    </p>
                  </div>

                  {release.notesUrl && (
                    <a
                      href={release.notesUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-primary hover:underline"
                    >
                      Release notes
                    </a>
                  )}

                  <Button
                    variant={release.version === info.available?.version ? 'primary' : 'secondary'}
                    className="h-8 px-2 text-sm"
                    disabled={
                      busy || gated || (blocked && release.version !== info.available?.version)
                    }
                    onClick={() => {
                      if (
                        confirm(
                          `Upgrade to ${release.version}?\n\n` +
                            'The PBX is backed up first, the call engine is drained, and calls still ' +
                            'inside it end when it restarts.\n\n' +
                            'If the new version fails its health check it is rolled back automatically.',
                        )
                      ) {
                        install.mutate(release.version);
                      }
                    }}
                  >
                    Upgrade now
                  </Button>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {settings && (
        <Section title="Automatic updates">
          <Grid>
            <Row label="When a new version appears">
              <Select
                value={settings.policy}
                onChange={(policy) =>
                  setForm({ ...settings, policy: policy as UpdateInfo['settings']['policy'] })
                }
              >
                <option value="never">Never check for updates</option>
                <option value="notify">Check for updates and notify me</option>
                <option value="auto">Check and install automatically</option>
              </Select>
            </Row>
            <Row
              label="Channel"
              hint="Beta releases are for testing, not for a PBX carrying calls."
            >
              <Select
                value={settings.channel}
                onChange={(channel) =>
                  setForm({ ...settings, channel: channel as 'stable' | 'beta' })
                }
              >
                <option value="stable">Stable</option>
                <option value="beta">Beta</option>
              </Select>
            </Row>
            <Row label="Check at" hint="Local time.">
              <Input
                type="time"
                value={settings.checkAt}
                onChange={(e) => setForm({ ...settings, checkAt: e.target.value })}
                disabled={settings.policy === 'never'}
              />
            </Row>
          </Grid>

          {settings.policy === 'auto' && (
            <Alert>
              <strong>Automatic installation restarts this PBX without asking.</strong> Calls inside
              the call engine end when it restarts. Most sites are better served by "notify me" and
              a chosen moment.
            </Alert>
          )}

          <FormFooter
            onCancel={form === null ? undefined : () => setForm(null)}
            saving={save.isPending}
            disabled={form === null}
          >
            <Button
              variant="secondary"
              disabled={form === null || save.isPending}
              onClick={() => save.mutate()}
            >
              Save policy
            </Button>
          </FormFooter>
        </Section>
      )}

      <Section title="Upgrading Asterisk" description="Only when a release includes it.">
        <p className="text-sm text-muted-foreground">
          Restarting Asterisk rebinds its SIP transports, which ends every call and re-registers
          every phone. A release only replaces it when it has to, and the upgrade says so before it
          starts.
        </p>
      </Section>
    </div>
  );
}

interface RebootStatus {
  activeCalls: number;
  inProgress: boolean;
  schedule: {
    enabled: boolean;
    frequency: 'daily' | 'weekly' | 'monthly';
    dayOfMonth: number;
    dayOfWeek: number;
    time: string;
    timezone: string;
    nextRunAt: string | null;
  };
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Restarting the PBX.
 *
 * The word "reboot" on an appliance means the whole box power-cycles. Here the PBX is a set of
 * containers on a host that may be running other things, so this restarts every klinkpbx service
 * and leaves the host alone. The screen says which of those it is doing rather than letting
 * somebody assume the stronger one — an administrator who thinks the machine has been power-cycled
 * will not go looking for the fault that survived.
 */
function RebootPanel() {
  const queryClient = useQueryClient();
  const [note, setNote] = useState<string | null>(null);
  const [rebooting, setRebooting] = useState(false);

  const { data: status } = useQuery({
    queryKey: ['reboot-status'],
    queryFn: () => api.get<RebootStatus>('/api/v1/system/reboot'),
    // While a reboot is running the API stops answering and then comes back; polling is how the
    // page notices the second half of that without the operator reloading and wondering.
    refetchInterval: rebooting ? 3000 : false,
    retry: rebooting,
  });

  const [form, setForm] = useState<RebootStatus['schedule'] | null>(null);
  const schedule = form ?? status?.schedule ?? null;

  const reboot = useMutation({
    mutationFn: (mode: 'graceful' | 'now') =>
      api.post<{ activeCalls: number }>('/api/v1/system/reboot', { mode, confirm: 'reboot' }),
    onSuccess: (result, mode) => {
      setRebooting(true);
      setNote(
        mode === 'graceful' && result.activeCalls > 0
          ? `Waiting for ${result.activeCalls} call(s) to finish, then restarting.`
          : 'Restarting now. This page reconnects on its own when the API is back.',
      );
    },
  });

  const save = useMutation({
    mutationFn: () =>
      api.put('/api/v1/system/reboot/schedule', {
        autoRebootEnabled: schedule!.enabled,
        frequency: schedule!.frequency,
        dayOfMonth: schedule!.dayOfMonth,
        dayOfWeek: schedule!.dayOfWeek,
        time: schedule!.time,
      }),
    onSuccess: () => {
      setForm(null);
      setNote('Reboot schedule saved.');
      void queryClient.invalidateQueries({ queryKey: ['reboot-status'] });
    },
  });

  const active = status?.activeCalls ?? 0;
  const patch = (next: Partial<RebootStatus['schedule']>) =>
    schedule && setForm({ ...schedule, ...next });

  return (
    <div className="space-y-5">
      {note && <Alert>{note}</Alert>}
      {reboot.error && <Alert>{(reboot.error as Error).message}</Alert>}

      <Section
        title="Reboot now"
        description="Restarts Asterisk, the call engine and the API. The host machine is not touched."
      >
        <div className="flex flex-wrap items-center gap-3">
          <Button
            disabled={rebooting || reboot.isPending}
            onClick={() => {
              if (
                confirm(
                  active > 0
                    ? `Reboot when the ${active} call(s) in progress have finished?\n\n` +
                        'No new calls are accepted from the moment you confirm.'
                    : 'Reboot the PBX now?\n\nThere are no calls in progress.',
                )
              ) {
                reboot.mutate('graceful');
              }
            }}
          >
            <RotateCcw className="mr-1.5 size-4" />
            {active > 0 ? 'Reboot when calls finish' : 'Reboot now'}
          </Button>

          {active > 0 && (
            <Button
              variant="secondary"
              disabled={rebooting || reboot.isPending}
              onClick={() => {
                if (
                  confirm(
                    `End ${active} call(s) in progress and reboot immediately?\n\n` +
                      'Everybody on a call right now is cut off.',
                  )
                ) {
                  reboot.mutate('now');
                }
              }}
            >
              <Power className="mr-1.5 size-4" />
              Reboot immediately
            </Button>
          )}

          <span className="text-sm text-muted-foreground">
            {rebooting
              ? 'Rebooting…'
              : active === 0
                ? 'No calls in progress.'
                : `${active} call(s) in progress.`}
          </span>
        </div>

        {/*
          The honest version of "Shut Down Now". Powering the machine off means stopping the
          containers this page is served from, which needs the Docker socket — and handing that to
          the API turns any flaw in it into root on the host. That trade was refused for upgrades
          and it is refused here for the same reason.
        */}
        <div className="mt-5 border-t border-border pt-4">
          <p className="text-sm font-medium">Shutting down</p>
          <p className="mt-1 text-sm text-muted-foreground">
            There is no shut-down button, on purpose. Stopping the PBX means stopping the container
            this page is served from, which needs access to the Docker socket — and giving that to
            the API would turn any flaw in it into root on the host. Run this on the host instead:
          </p>
          <pre className="mt-3 overflow-x-auto rounded-[--radius] border border-border bg-muted/40 p-3 text-sm">
            <code>klinkpbx stop</code>
          </pre>
          <p className="mt-2 text-sm text-muted-foreground">
            It stays down until <code>klinkpbx start</code>. A reboot, above, does not need the
            host.
          </p>
        </div>
      </Section>

      <Section
        title="Reboot schedule"
        description="A restart in the small hours costs nothing and clears anything that has drifted since the last one."
      >
        {schedule && (
          <>
            <div className="flex items-center gap-3">
              <Toggle
                checked={schedule.enabled}
                onChange={(enabled) => patch({ enabled })}
                label="Enable auto reboot"
              />
              <span className="text-sm">Enable auto reboot</span>
            </div>

            <Grid>
              <Row label="Frequency">
                <Select
                  value={schedule.frequency}
                  onChange={(frequency) =>
                    patch({ frequency: frequency as RebootStatus['schedule']['frequency'] })
                  }
                  disabled={!schedule.enabled}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </Select>
              </Row>

              {schedule.frequency === 'monthly' && (
                <Row
                  label="Date"
                  hint="A date past the end of a short month runs on that month's last day."
                >
                  <Select
                    value={String(schedule.dayOfMonth)}
                    onChange={(v) => patch({ dayOfMonth: Number(v) })}
                    disabled={!schedule.enabled}
                  >
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </Select>
                </Row>
              )}

              {schedule.frequency === 'weekly' && (
                <Row label="Day">
                  <Select
                    value={String(schedule.dayOfWeek)}
                    onChange={(v) => patch({ dayOfWeek: Number(v) })}
                    disabled={!schedule.enabled}
                  >
                    {WEEKDAYS.map((name, i) => (
                      <option key={name} value={i}>
                        {name}
                      </option>
                    ))}
                  </Select>
                </Row>
              )}

              <Row label="Time" hint={`In ${schedule.timezone}, the system timezone.`}>
                <Input
                  type="time"
                  value={schedule.time}
                  onChange={(e) => patch({ time: e.target.value })}
                  disabled={!schedule.enabled}
                />
              </Row>
            </Grid>

            {schedule.enabled && status?.schedule.nextRunAt && form === null && (
              <p className="text-sm text-muted-foreground">
                Next restart: <strong>{status.schedule.nextRunAt}</strong> ({schedule.timezone}).
                Calls in progress are waited for.
              </p>
            )}

            {save.error ? <Alert>{(save.error as Error).message}</Alert> : null}

            <FormFooter
              onCancel={form === null ? undefined : () => setForm(null)}
              saving={save.isPending}
              disabled={form === null}
            />
          </>
        )}
      </Section>
    </div>
  );
}
